import * as React from "react";
import { HTMLTable, HTMLSelect, Button, Callout, Alert, Intent } from "@blueprintjs/core";
import { css } from "@emotion/core";
import { FolderGroupMultiSelect } from "./FolderGroupMultiSelect";
import { IPermissionType } from "../../../api/admin";
import { PERMISSION_LEVEL_VIEW } from "../../../constants";
import { useItemsById } from "../../../hooks/useSelectedItems";

interface GroupsPanelProps {
  selectedGroupIds: number[];
  groups: any[];
  permissionTypes: IPermissionType[];
  groupPermissions: Map<number, number>;
  onGroupChange: (groupIds: number[]) => void;
  onGroupRemove: (groupId: number) => Promise<void>;
  handleGroupPermissionChange: (
    groupId: number,
    accessLevel: number
  ) => void;
  isRemoving?: boolean;
}

export function GroupsPanel(props: GroupsPanelProps) {
  const {
    selectedGroupIds,
    groups,
    permissionTypes,
    groupPermissions,
    onGroupChange,
    onGroupRemove,
    handleGroupPermissionChange,
    isRemoving = false,
  } = props;

  const groupsById = useItemsById(groups);

  const [pendingRemoveGroupId, setPendingRemoveGroupId] = React.useState<number | null>(null);

  const handleRemoveClick = (groupId: number) => {
    setPendingRemoveGroupId(groupId);
  };

  const handleConfirmRemove = async () => {
    if (pendingRemoveGroupId !== null) {
      await onGroupRemove(pendingRemoveGroupId);
      // Only close the alert after removal completes (success or error)
      setPendingRemoveGroupId(null);
    }
  };

  const handleCancelRemove = () => {
    setPendingRemoveGroupId(null);
  };

  const pendingGroup = pendingRemoveGroupId !== null ? groupsById.get(pendingRemoveGroupId) : null;
  const pendingGroupName = pendingGroup?.name || (pendingRemoveGroupId !== null ? `Group ${pendingRemoveGroupId}` : "");

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 1rem;
        height: 50vh;
      `}
    >
      <div
        css={css`
          padding: 0 1rem;
        `}
      >
        <FolderGroupMultiSelect
          selectedGroupIds={selectedGroupIds}
          groups={groups}
          onGroupChange={onGroupChange}
        />
      </div>
      <div
        css={css`
          margin-top: 1rem;
          background-color: var(--neutral-8);
          border-radius: 4px;
          border-top: 1px solid var(--neutral-5);
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        `}
      >
        {selectedGroupIds.length === 0 ? (
          <div
            css={css`
              padding: 1rem;
              color: var(--neutral-4);
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <div
              css={css`
                width: 100%;
                max-width: 48ch;
              `}
            >
              <Callout intent="primary" title="No groups selected">
                <p
                  css={css`
                    margin-bottom: 1rem;
                  `}
                >
                  Select a group from above to add it to this folder.
                </p>
              </Callout>
            </div>
          </div>
        ) : (
          <>
            <div>
              <HTMLTable
                striped
                interactive
                css={css`
                  width: 100%;
                `}
              >
                <colgroup>
                  <col />
                  <col width="200px" />
                  <col width="62px" />
                </colgroup>
                <thead
                  css={css`
                    box-shadow: inset 0 -1px rgba(255, 255, 255, 0.2);
                  `}
                >
                  <tr>
                    <th>
                      <div
                        css={css`
                          padding: 0.25rem 5px;
                        `}
                      >
                        Group Name
                      </div>
                    </th>
                    <th>
                      <div
                        css={css`
                          padding: 0.25rem;
                        `}
                      >
                        Permission
                      </div>
                    </th>
                    <th></th>
                  </tr>
                </thead>
              </HTMLTable>
            </div>
            <div
              css={css`
                overflow: auto;
                flex: 1;

                & table.bp6-html-table tbody tr:first-of-type td {
                  box-shadow: none;
                }
              `}
            >
              <HTMLTable
                striped
                interactive
                css={css`
                  width: 100%;
                `}
              >
                <colgroup>
                  <col />
                  <col width="200px" />
                  <col width="42px" />
                </colgroup>
                <tbody>
                  {selectedGroupIds.map((groupId) => {
                    const group = groupsById.get(groupId);
                    const currentPermissionLevel =
                      groupPermissions.get(groupId) ||
                      permissionTypes.find(
                        (pt) => pt.level === PERMISSION_LEVEL_VIEW
                      )?.level ||
                      PERMISSION_LEVEL_VIEW;

                    return (
                      <tr key={groupId}>
                        <td>
                          <div
                            css={css`
                              font-size: 0.875rem;
                              padding: 0.25rem 5px;
                            `}
                          >
                            {group?.name || `Group ${groupId}`}
                          </div>
                        </td>
                        <td>
                          <HTMLSelect
                            css={css`
                              width: 100%;
                            `}
                            value={currentPermissionLevel}
                            disabled={group?.autogenerated === true}
                            onChange={(e) =>
                              handleGroupPermissionChange(
                                groupId,
                                Number(e.target.value)
                              )
                            }
                          >
                            {permissionTypes.map((pt) => (
                              <option key={pt.id} value={pt.level}>
                                {pt.label}
                              </option>
                            ))}
                          </HTMLSelect>
                        </td>
                        <td>
                          <div
                            css={css`
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              padding: 0 5px;
                            `}
                          >
                            {!group?.autogenerated && (
                              <Button
                                icon="cross"
                                intent="danger"
                                onClick={() => handleRemoveClick(groupId)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </HTMLTable>
            </div>
          </>
        )}
      </div>
      <Alert
        className="bp6-dark"
        isOpen={pendingRemoveGroupId !== null}
        icon="warning-sign"
        intent={Intent.DANGER}
        cancelButtonText="Cancel"
        confirmButtonText={isRemoving ? "Removing..." : "Remove"}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        canEscapeKeyClose={!isRemoving}
        canOutsideClickClose={!isRemoving}
      >
        <p>
          Are you sure you want to remove <strong>{pendingGroupName}</strong>? This will remove their access to this folder.
        </p>
      </Alert>
    </div>
  );
}
